// RichEditor.jsx
import React, { useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import {
  Bold,
  Italic,
  Strikethrough,
  ListOrdered,
  List,
  Link as LinkIcon,
  Quote,
  Code,
  Image as ImageIcon,
  Undo2,
  Redo2,
} from "lucide-react";

export default function RichEditor({
  value = "", // HTML
  onChange = () => {},
  placeholder = "Write your content…",
  maxChars = 100000, // optional soft-limit display
  className = "",
}) {
  const ACCENT = "#169FA7";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
        codeBlock: true,
        blockquote: true,
      }),
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
      Image,
      Placeholder.configure({ placeholder }),
      CharacterCount.configure({ limit: maxChars }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none dark:prose-invert focus:outline-none " +
          "prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-code:text-[0.9em] " +
          "prose-img:rounded-xl prose-img:my-3 " +
          "text-neutral-900 dark:text-neutral-100",
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  const can = useMemo(() => {
    if (!editor) return {};
    return {
      undo: editor.can().undo(),
      redo: editor.can().redo(),
      bold: editor.can().chain().focus().toggleBold().run(),
      italic: editor.can().chain().focus().toggleItalic().run(),
      strike: editor.can().chain().focus().toggleStrike().run(),
      bullet: editor.can().chain().focus().toggleBulletList().run(),
      ordered: editor.can().chain().focus().toggleOrderedList().run(),
      blockquote: editor.can().chain().focus().toggleBlockquote().run(),
      code: editor.can().chain().focus().toggleCodeBlock().run(),
      link: true,
      image: true,
    };
  }, [editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link")?.href || "";
    const url = window.prompt("Enter URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Paste image URL");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const Btn = ({ onClick, active, disabled, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={
        "h-8 px-2 rounded-lg text-sm inline-flex items-center gap-1 border " +
        (active
          ? "border-transparent text-white"
          : "border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200") +
        " disabled:opacity-40"
      }
      style={active ? { backgroundColor: ACCENT } : {}}
    >
      {children}
    </button>
  );

  return (
    <div
      className={
        "rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/80 " +
        className
      }
    >
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1.5 p-2 border-b border-neutral-200 dark:border-neutral-800">
        <Btn
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          disabled={!can.bold}
        >
          <Bold size={16} />
        </Btn>

        <Btn
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          disabled={!can.italic}
        >
          <Italic size={16} />
        </Btn>

        <Btn
          title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          disabled={!can.strike}
        >
          <Strikethrough size={16} />
        </Btn>

        <div className="w-px h-6 mx-1 bg-neutral-200 dark:bg-neutral-800" />

        <Btn
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          disabled={!can.bullet}
        >
          <List size={16} />
        </Btn>

        <Btn
          title="Ordered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          disabled={!can.ordered}
        >
          <ListOrdered size={16} />
        </Btn>

        <div className="w-px h-6 mx-1 bg-neutral-200 dark:bg-neutral-800" />

        <Btn
          title="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          disabled={!can.blockquote}
        >
          <Quote size={16} />
        </Btn>

        <Btn
          title="Code block"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive("codeBlock")}
          disabled={!can.code}
        >
          <Code size={16} />
        </Btn>

        <div className="w-px h-6 mx-1 bg-neutral-200 dark:bg-neutral-800" />

        <Btn
          title="Link"
          onClick={setLink}
          active={editor.isActive("link")}
          disabled={!can.link}
        >
          <LinkIcon size={16} />
        </Btn>

        <Btn title="Image" onClick={addImage} disabled={!can.image}>
          <ImageIcon size={16} />
        </Btn>

        <div className="ml-auto flex gap-1.5">
          <Btn
            title="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!can.undo}
          >
            <Undo2 size={16} />
          </Btn>
          <Btn
            title="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!can.redo}
          >
            <Redo2 size={16} />
          </Btn>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        <EditorContent editor={editor} className="min-h-[260px] px-1 py-1" />
      </div>

      {/* Footer / character count */}
      <div className="flex items-center justify-end gap-3 px-3 py-2 border-t border-neutral-200 dark:border-neutral-800 text-xs text-neutral-500">
        <span>{editor.storage.characterCount.characters()} chars</span>
        <span>•</span>
        <span>{editor.storage.characterCount.words()} words</span>
      </div>
    </div>
  );
}
